import {
    FCX, cssX, getRem,
    IDropdownColumn, includesInLC, markMatch,
    SearchBox, SearchBoxLoading,
    SearchBoxNoResults, startsFromInLC,
    usePlatformFeatureFlag, useUuid
} from "@datassential/platform-ui-lib";
import { useCallback, useMemo, useState } from "react";
import cn from "classnames";
import qs from 'query-string';
import { useCommonDataContext, useKeywords } from "@core/CommonDataContext";
import { Link, matchPath, useLocation, useSearchParams } from "react-router-dom";
import { getKeywordProfileLink, Routes } from "@core/routes";
import ToolSearch from "@vms/ToolSearch";
import { FEATURE_AI_SEARCH } from "@core/constants";

const Search: FCX = () => {
    const searchString = qs.parse(window.location.search).SearchString as string ?? '';
    const [query, setQuery] = useState<string>(searchString);
    const [isToolSearchOpened, setIsToolSearchOpened] = useState(false);
    const searchDropdownId = useUuid();
    const { isLoading } = useCommonDataContext();
    const rawKeywords = useKeywords();
    const location = useLocation();
    const [searchParams] = useSearchParams(location.search);
    const isAiSearchEnabled = usePlatformFeatureFlag(FEATURE_AI_SEARCH);

    const currentKeywordId = useMemo(
        () => {
            if (matchPath(location.pathname, Routes.Profile)) {
                return searchParams.get('keywordId') ?? '';
            }

            return '';
        },
        [searchParams, location.pathname]
    );

    const preSortedKeywords = useMemo(
        () => [...rawKeywords]
            .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''))
            .filter(i => `${i.id}` !== currentKeywordId),
        [rawKeywords, currentKeywordId]
    );

    const keywords = useMemo(
        () => [
            ...preSortedKeywords.filter(i => startsFromInLC(i.name ?? '', query.trim().toLowerCase())),
            ...preSortedKeywords.filter(i => !startsFromInLC(i.name ?? '', query.trim().toLowerCase()) && includesInLC(i.name ?? '', query.trim().toLowerCase())),
        ].slice(0, 10),
        [preSortedKeywords, query]
    );

    const openToolSearch = useCallback(() => setIsToolSearchOpened(true), []);
    const closeToolSearch = useCallback(() => setIsToolSearchOpened(false), []);

    const searchAutocompleteGroups = useMemo(
        (): IDropdownColumn[] | undefined => {
            return [
                {
                    id: 'keywords',
                    items: keywords.map((item) => ({
                        id: item.id,
                        content: (
                            <Link
                                to={getKeywordProfileLink(item?.id)}
                                className={cn(cssX("Dropdown__item"), 'js-btn-search')}
                                onClick={() => {
                                    setQuery('');
                                }}
                            >
                                <div dangerouslySetInnerHTML={{
                                    __html: markMatch(item.name ?? '', query.trim())
                                }}/>
                            </Link>
                        ),
                    })),
                }
            ];
        },
        [query, keywords]
    );

    const children = useMemo(
        () => {
            if (isLoading) return (
                <SearchBoxLoading/>
            );

            if (searchAutocompleteGroups === undefined) return (
                <SearchBoxNoResults/>
            );

            return null;
        },
        [isLoading, searchAutocompleteGroups]
    );

    const inputProps = useMemo(() => {
        const commonProps = {
            placeholder: "Search anything...",
            testId: "inputHeaderSearch",
        };

        if (isAiSearchEnabled) {
            return {
                ...commonProps,
                value: "",
                setValue: () => null,
                style: { pointerEvents: "none" } as const
            };
        }

        return {
            ...commonProps,
            value: query,
            setValue: (value: string) => setQuery(value)
        };
    }, [isAiSearchEnabled, query]);

    const dropdownProps = useMemo(() => {
        const commonProps = {
            testId: "selectHeaderSearchDropdown"
        };

        if (isAiSearchEnabled) {
            return {
                ...commonProps,
                id: "no-data",
                isOpened: false
            };
        }

        return {
            ...commonProps,
            id: searchDropdownId,
            isOpened: !!query.trim(),
            groups: searchAutocompleteGroups,
            style: { maxHeight: getRem(300), overflowX: 'hidden' } as const,
            children
        };
    }, [isAiSearchEnabled, searchDropdownId, query, searchAutocompleteGroups, children]);

    return (
        <>
            <SearchBox
                testId="selectHeaderSearch"
                tooltipId={searchDropdownId}
                inputProps={inputProps}
                dropdownProps={dropdownProps}
                onClick={isAiSearchEnabled ? openToolSearch : undefined}
                style={isAiSearchEnabled ? { cursor: "pointer" } : undefined}
            />
            <ToolSearch isOpen={isToolSearchOpened} close={closeToolSearch}/>
        </>
    );
};

export default Search;